/**
 * 路由权限控制
 * 参照xht-web项目的实现方式
 */
import router from './router'
import store from './store'
import { isLoginValid } from '@/utils/user'

// 白名单路由，不需要登录即可访问
// /coupon/qr 是二维码扫描页面，允许免登录访问（token 会从二维码 payload 中提取）
// /vehicle/invite 是邀请他人填写车牌页面，允许免登录访问（但提交时需要登录）
const whiteList = ['/login', '/coupon/qr', '/vehicle/invite']

// 判断路径是否在白名单中（支持精确匹配和前缀匹配）
// 注意：在 hash 路由模式下，需要同时检查 to.path 和 window.location.pathname
function isInWhiteList(path, fullPath) {
  // 精确匹配
  if (whiteList.includes(path)) {
    return true
  }
  // 前缀匹配：/coupon/qr 开头的路径都允许访问（支持带参数的情况）
  if (path.startsWith('/coupon/qr')) {
    return true
  }
  // 前缀匹配：/vehicle/invite 开头的路径都允许访问（支持带参数的情况）
  if (path.startsWith('/vehicle/invite')) {
    return true
  }
  // 检查完整路径（hash 路由模式下，fullPath 可能包含更多信息）
  if (fullPath && fullPath.includes('/coupon/qr')) {
    return true
  }
  if (fullPath && fullPath.includes('/vehicle/invite')) {
    return true
  }
  // 检查 window.location.pathname（对于 hash 路由，pathname 是基础路径）
  if (typeof window !== 'undefined' && window.location) {
    const pathname = window.location.pathname
    if (pathname.includes('/coupon/qr')) {
      return true
    }
    if (pathname.includes('/vehicle/invite')) {
      return true
    }
  }
  return false
}

// 路由守卫
router.beforeEach(async (to, from, next) => {
  console.log('[permission] 路由守卫触发:', {
    to: to.path,
    from: from.path,
    fullPath: to.fullPath,
    hash: to.hash,
    pathname: typeof window !== 'undefined' ? window.location.pathname : 'N/A',
    search: typeof window !== 'undefined' ? window.location.search : 'N/A',
    href: typeof window !== 'undefined' ? window.location.href : 'N/A'
  })
  
  // 白名单路由直接放行，不进行token验证和用户信息获取
  // 特别是 /coupon/qr 和 /vehicle/invite 页面，不需要登录即可访问
  // 使用 isInWhiteList 函数支持路径前缀匹配，确保白名单路径及其所有子路径都可以访问
  // 必须在最前面检查，避免其他逻辑干扰
  
  // 优先检查：如果 pathname 是白名单路径，无论 to.path 是什么，都应该重定向到正确的白名单路径
  // 这必须在所有其他检查之前执行，确保白名单路径不会被误判
  if (typeof window !== 'undefined' && window.location) {
    const pathname = window.location.pathname
    const search = window.location.search
    
    // 检查 pathname 是否是白名单路径
    if (pathname === '/coupon/qr' || pathname.startsWith('/coupon/qr')) {
      // 如果 to.path 不是白名单路径，需要重定向
      if (to.path !== '/coupon/qr' && !to.path.startsWith('/coupon/qr')) {
        console.log('[permission] pathname 是 /coupon/qr，但 to.path 不是，重定向到正确路径')
        let query = {}
        try {
          query = Object.fromEntries(new URLSearchParams(search))
        } catch (e) {
          console.warn('[permission] 解析查询参数失败:', e)
        }
        next({ path: '/coupon/qr', query })
        return
      }
    } else if (pathname === '/vehicle/invite' || pathname.startsWith('/vehicle/invite')) {
      // 如果 to.path 不是白名单路径，需要重定向
      if (to.path !== '/vehicle/invite' && !to.path.startsWith('/vehicle/invite')) {
        console.log('[permission] pathname 是 /vehicle/invite，但 to.path 不是，重定向到正确路径')
        let query = {}
        try {
          query = Object.fromEntries(new URLSearchParams(search))
        } catch (e) {
          console.warn('[permission] 解析查询参数失败:', e)
        }
        next({ path: '/vehicle/invite', query })
        return
      }
    }
  }
  
  // 白名单路由直接放行，不进行token验证和用户信息获取
  const isWhiteListRoute = isInWhiteList(to.path, to.fullPath)
  
  console.log('[permission] 白名单检查:', {
    toPath: to.path,
    fullPath: to.fullPath,
    isWhiteListRoute: isWhiteListRoute,
    whiteList: whiteList
  })
  
  if (isWhiteListRoute) {
    console.log('[permission] 白名单路由，直接放行:', {
      toPath: to.path,
      fullPath: to.fullPath
    })
    next()
    return
  }
  
  const token = store.getters.token || localStorage.getItem('token')
  
  // 如果路由到登录页面，检查是否有有效的登录凭证
  if (to.path === '/login') {
    // 如果有有效的token（2天内），直接重定向到首页
    if (token && isLoginValid()) {
      console.log('[permission] 检测到有效的登录凭证，重定向到首页')
      next({ path: '/home' })
      return
    }
    // 如果没有有效的token，清空用户缓存
    console.log('=== 路由到登录页面，清空用户缓存 ===')
    localStorage.removeItem('userInfo')
    localStorage.removeItem('userId')
    store.commit('CLEAR_USER')
    console.log('用户缓存已清空')
    console.log('==============================')
  }
  
  if (token) {
    // 检查token是否有效（2天内）
    if (!isLoginValid()) {
      // token无效或过期，清除并重定向到登录页
      console.log('[permission] token无效或过期，清除并重定向到登录页')
      store.dispatch('Logout')
      next({ path: '/login' })
      return
    }
    
    if (to.path === '/login') {
      // 已登录，重定向到首页
      next({ path: '/home' })
    } else {
      // 检查是否有用户信息
      if (!store.getters.userInfo || Object.keys(store.getters.userInfo).length === 0) {
        try {
          // 获取用户信息
          await store.dispatch('GetInfo')
          // 同步 userId 至 localStorage（兜底）
          const ui = store.getters.userInfo || {}
          if (ui && ui.userId) localStorage.setItem('userId', String(ui.userId))
        } catch (error) {
          console.error('获取用户信息失败:', error)
          // 清除token，重定向到登录页
          store.dispatch('Logout')
          next({ path: '/login' })
          return
        }
      }
      next()
    }
  } else {
    // 没有token，重定向到登录页
    console.log('[permission] 没有token，重定向到登录页')
    next({ path: '/login' })
  }
})

export default router
